import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { last } from 'ramda';

import { selectLocation } from '../store/location/selectors';
import { removeToast } from '../store/toasts/actions';
import { selectToastsByContext } from '../store/toasts/selectors';

/**
 * Keeps a reference to the last context-specific toast until the
 * component is unmounted or the location pathname changes.
 *
 * The main intended purpose is to be able to show a message until
 * the user navigates away from a particular page.
 *
 * This is an alternative to passing the message in the query string,
 * which can inadvertently show up on future visits (for example, by
 * usage of the browser Back button).
 *
 * @param {string} context - The toast context to filter the toasts with.
 * @returns {object} The last "local" context toast, if one is present.
 */
export const useLastToast = (context) => {
  const location = useSelector(selectLocation);
  const pathname = location?.pathname;
  const toasts = useSelector(selectToastsByContext(context));
  const toast = last(toasts);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      if (pathname && toast?.id) dispatch(removeToast(toast.id));
    };
  }, [dispatch, pathname, toast]);

  return toast;
};
